/**
 * RBAC v2 — frontend permission catalog (mirror of backend/models/rbac.py).
 *
 * Use with useAuth().hasPermission(PERM.CAMPAIGNS_MANAGE) etc. The authoritative
 * catalog for the role-builder UI is fetched from GET /api/roles/catalog; this
 * file provides stable keys for gating UI in components.
 */

export const VIEW = 'view';
export const MANAGE = 'manage';

/** Feature keys that support view/manage. */
export const FEATURES = [
  'accounts',
  'communications',
  'campaigns',
  'workflows',
  'onboarding',
  'analytics',
  'advocacy',
  'meetings',
  'tasks',
  'business_profile',
  'products',
  'integrations',
  'knowledge_base',
  'assistant',
];

/** High-risk admin capabilities (single toggle each). */
export const ADMIN_CAPABILITIES = [
  'users_roles',
  'billing',
  'company_details',
  'ai_settings',
  'health_thresholds',
  'account_assignments',
  'team_routing',
  'api_webhooks',
  'data_export',
  'data_deletion',
];

export const feature = (key, level) => `${key}:${level}`;
export const admin = (cap) => `admin:${cap}`;

/** System role slugs (mirror of backend rbac constants). */
export const ROLE = {
  ADMIN: 'admin',
  MANAGER: 'manager',
  ACCOUNT_OWNER: 'account_owner',
  ONBOARDING_SPECIALIST: 'onboarding_specialist',
};

/**
 * Effective roles allowed to be an account's Primary Owner. CSM/AM job
 * functions grant the Account Owner role, so this covers them plus Admin/Manager.
 */
export const OWNER_ELIGIBLE_ROLES = [ROLE.ADMIN, ROLE.MANAGER, ROLE.ACCOUNT_OWNER];

/** True if a user's (effective) role slug can be an account Primary Owner. */
export const isOwnerEligibleRole = (role) => OWNER_ELIGIBLE_ROLES.includes(role);

/** True if a user is an org-wide admin or manager. */
export const isAdminOrManager = (role) => role === ROLE.ADMIN || role === ROLE.MANAGER;

/** Named permission constants used across components. */
export const PERM = {
  ACCOUNTS_VIEW: 'accounts:view',
  ACCOUNTS_MANAGE: 'accounts:manage',
  COMMUNICATIONS_VIEW: 'communications:view',
  COMMUNICATIONS_MANAGE: 'communications:manage',
  CAMPAIGNS_VIEW: 'campaigns:view',
  CAMPAIGNS_MANAGE: 'campaigns:manage',
  WORKFLOWS_VIEW: 'workflows:view',
  WORKFLOWS_MANAGE: 'workflows:manage',
  ONBOARDING_VIEW: 'onboarding:view',
  ONBOARDING_MANAGE: 'onboarding:manage',
  ANALYTICS_VIEW: 'analytics:view',
  ANALYTICS_MANAGE: 'analytics:manage',
  ADVOCACY_VIEW: 'advocacy:view',
  ADVOCACY_MANAGE: 'advocacy:manage',
  MEETINGS_VIEW: 'meetings:view',
  MEETINGS_MANAGE: 'meetings:manage',
  TASKS_VIEW: 'tasks:view',
  TASKS_MANAGE: 'tasks:manage',
  BUSINESS_PROFILE_VIEW: 'business_profile:view',
  BUSINESS_PROFILE_MANAGE: 'business_profile:manage',
  PRODUCTS_VIEW: 'products:view',
  PRODUCTS_MANAGE: 'products:manage',
  INTEGRATIONS_VIEW: 'integrations:view',
  INTEGRATIONS_MANAGE: 'integrations:manage',
  KNOWLEDGE_BASE_VIEW: 'knowledge_base:view',
  KNOWLEDGE_BASE_MANAGE: 'knowledge_base:manage',
  ASSISTANT_VIEW: 'assistant:view',
  ASSISTANT_MANAGE: 'assistant:manage',
  // Admin capabilities
  ADMIN_USERS_ROLES: 'admin:users_roles',
  ADMIN_BILLING: 'admin:billing',
  ADMIN_COMPANY_DETAILS: 'admin:company_details',
  ADMIN_AI_SETTINGS: 'admin:ai_settings',
  ADMIN_HEALTH_SCORE_CONFIGURATION: 'admin:health_thresholds',
  ADMIN_HEALTH_THRESHOLDS: 'admin:health_thresholds',
  ADMIN_ACCOUNT_ASSIGNMENTS: 'admin:account_assignments',
  ADMIN_TEAM_ROUTING: 'admin:team_routing',
  ADMIN_API_WEBHOOKS: 'admin:api_webhooks',
  ADMIN_DATA_EXPORT: 'admin:data_export',
  ADMIN_DATA_DELETION: 'admin:data_deletion',
  ADMIN_SECURITY_SETTINGS: 'admin:security_settings',
};
